import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Alert, LinearProgress } from '@mui/material';
import { PlaywrightTestTree } from './components/PlaywrightTestTree';
import { PlaywrightTestDetail } from './components/PlaywrightTestDetail';

export interface PlaywrightTest {
  id: string;
  name: string;
  file: string;
  role: string;
  tags: string[];
  status: 'passed' | 'failed' | 'skipped' | 'not-run';
  duration?: number;
  error?: string;
  artifacts?: {
    screenshots: string[];
    videos: string[];
    trace?: string;
  };
}

export interface PlaywrightTestGroup {
  role: string;
  tags: {
    [tag: string]: PlaywrightTest[];
  };
}

export const PlaywrightTests: React.FC = () => {
  const [testGroups, setTestGroups] = useState<PlaywrightTestGroup[]>([]);
  const [selectedTest, setSelectedTest] = useState<PlaywrightTest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runOutput, setRunOutput] = useState<string[]>([]);

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('http://localhost:9999/api/playwright-tests');
      if (!res.ok) {
        throw new Error(`Failed to load tests: ${res.statusText}`);
      }
      const data = await res.json();
      setTestGroups(data.results || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunTests = async () => {
    setIsRunning(true);
    setRunOutput([]);
    setError(null);

    try {
      const response = await fetch('http://localhost:9999/api/playwright-run', {
        method: 'POST',
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Failed to start test execution');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n').filter(line => line.trim().startsWith('data:'));

        lines.forEach(line => {
          const data = line.substring(5).trim(); // Remove 'data:' prefix
          try {
            const event = JSON.parse(data);

            switch (event.type) {
              case 'started':
                setRunOutput(prev => [...prev, `Test execution started (${event.processId})`]);
                break;
              case 'output':
                setRunOutput(prev => [...prev, event.data]);
                break;
              case 'complete':
                setRunOutput(prev => [...prev, `\nTest execution completed with code ${event.code}`]);
                setIsRunning(false);
                // Reload test results after completion
                setTimeout(() => loadTests(), 1000);
                break;
              case 'error':
                setError(event.error);
                setIsRunning(false);
                break;
            }
          } catch (e) {
            // Ignore parse errors from incomplete JSON
          }
        });
      }
    } catch (err: any) {
      setError(err.message);
      setIsRunning(false);
    }
  };

  const getTotalTestCount = () => {
    return testGroups.reduce((sum, group) => {
      return sum + Object.values(group.tags).reduce((tagSum, tests) => tagSum + tests.length, 0);
    }, 0);
  };

  const getAverageDuration = () => {
    let totalDuration = 0;
    let testsWithDuration = 0;

    testGroups.forEach(group => {
      Object.values(group.tags).forEach(tests => {
        tests.forEach(test => {
          if (test.duration !== undefined) {
            totalDuration += test.duration;
            testsWithDuration++;
          }
        });
      });
    });

    return testsWithDuration > 0 ? totalDuration / testsWithDuration : 0;
  };

  const getEstimatedDuration = () => {
    const totalTests = getTotalTestCount();
    const avgDuration = getAverageDuration();

    // If we have historical data, use it
    if (avgDuration > 0) {
      return avgDuration * totalTests;
    }

    // Otherwise, estimate based on typical Playwright test duration
    // Smoke tests: ~2s, Workflow tests: ~5s
    // Assume average of 3.5s per test
    const estimatedAvg = 3500; // 3.5 seconds in ms
    return estimatedAvg * totalTests;
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `~${minutes}m ${remainingSeconds}s`;
    }
    return `~${seconds}s`;
  };

  const hasAnyTests = getTotalTestCount() > 0;
  const estimatedDuration = getEstimatedDuration();

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Loading Playwright tests...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadTests}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Playwright Tests</Typography>
          {hasAnyTests && (
            <Typography variant="caption" color="text.secondary">
              {getTotalTestCount()} tests · Est. {formatDuration(estimatedDuration)}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={handleRunTests}
          disabled={isRunning}
          color="primary"
        >
          {isRunning ? 'Running Tests...' : 'Run All Tests'}
        </Button>
      </Box>

      {!hasAnyTests && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Playwright test results found. Click "Run All Tests" to execute the test suite.
        </Alert>
      )}

      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">
            <Typography variant="body2" gutterBottom>
              Running Playwright tests... This may take a few minutes.
            </Typography>
          </Alert>
          {runOutput.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                borderRadius: 1,
                maxHeight: 400,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {runOutput.map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </Box>
          )}
        </Box>
      )}

      {selectedTest ? (
        <PlaywrightTestDetail
          test={selectedTest}
          onBack={() => setSelectedTest(null)}
          onRefresh={loadTests}
        />
      ) : (
        hasAnyTests && (
          <PlaywrightTestTree
            testGroups={testGroups}
            onSelectTest={setSelectedTest}
          />
        )
      )}
    </Box>
  );
};
